import type { Page } from "~/components/search/SearchResult"
import { isSchedulingActive } from "~/sdk/contentScheduling"

export interface Props {
  pages: Page[]
}

// Section loaders run inside `runSectionLoaders`, which swallows anything a
// loader throws — so this loader can't throw a router redirect itself. It
// returns this marker in its props instead, and the catch-all route loader
// (src/routes/$.tsx) turns it into a real redirect.
export interface SectionRedirect {
  href: string
  status: number
}

const RedirectDepartments = () => {
  return null
}

export function LoadingFallback() {
  return null
}

export const redirectWhenURLMatches = (request: Request) => {
  const { searchParams, pathname } = new URL(request.url)

  const mapParam = searchParams.get('map')

  if (mapParam) {
    const categoryDepthCount = (mapParam || '')
      .split(/[,;]/)
      .filter((seg) => seg === 'c').length

    const urlSegments = pathname.split('/').filter(Boolean)

    const redirectPathWhenNoProducts = urlSegments
      .slice(0, categoryDepthCount)
      .join('/')

    return {
      type: 'category_not_found' as const,
      path: `/${redirectPathWhenNoProducts}`,
      status: 301,
    }
  }

  return null
}

export const loader = (
  props: Props,
  request: Request,
) => {
  // `pages` (the full PLP) is only needed to decide the redirect; drop it from
  // the returned props so it isn't serialized into the router payload (the
  // component renders null and reads nothing).
  const { pages, ...rest } = props

  try {
    const page = (pages ?? []).find(({ scheduling }) => isSchedulingActive(scheduling))
      ?.page ?? null
    const hasProductsOnPage = (page?.products?.length ?? 0) > 0

    const matchRedirect = !hasProductsOnPage && redirectWhenURLMatches(request)

    if (matchRedirect) {
      return {
        ...rest,
        __redirect: { href: matchRedirect.path, status: matchRedirect.status } satisfies SectionRedirect,
      }
    }

    return rest
  } catch (error) {
    const message = String((error as Error)?.message || error)
    const isInvalidParam = message.includes('Invalid Parameter')

    const matchRedirect = redirectWhenURLMatches(request)

    if (matchRedirect) {
      return {
        ...rest,
        __redirect: { href: matchRedirect.path, status: matchRedirect.status } satisfies SectionRedirect,
      }
    }

    if (isInvalidParam) {
      const url = new URL(request.url)
      return {
        ...rest,
        __redirect: { href: url.pathname, status: 301 } satisfies SectionRedirect,
      }
    }

    return rest
  }
}

export default RedirectDepartments
